/**
 * Pet Flow State Manager
 * 
 * Manages the state of the pet buying flow
 * Single source of truth for all flow data
 */

/**
 * Trim leading/trailing whitespace, BOM, and NBSP from pet display name.
 * @param {string} value
 * @returns {string}
 */
function normalizePetNameEdges(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/^[\s\uFEFF\u00A0]+|[\s\uFEFF\u00A0]+$/g, '');
}
window.normalizePetNameEdges = normalizePetNameEdges;

class PetFlowState {
  constructor(flowName = 'wizard-1') {
    this.flowName = flowName;
    this.currentStep = 'name';
    this.petData = {
      // Step 1: Name
      name: '',
      gender: '', // 'male' or 'female'
      
      // Step 2: Spayed/Neutered
      spayed: null, // true or false
      
      // Step 3: Age
      ageGroup: '', // 'puppy', 'adult', 'senior'
      
      // Step 4: Breed
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      
      // Step 5: Weight
      weight: null, // in kg
      
      // Step 6: Composition
      composition: '', // 'heavy', 'normal', 'slim'
      
      // Step 7: Activity
      activity: '', // 'indoorInactive', 'low', 'moderate', 'veryActive'
      isWorkingDog: false,
      
      // Step 8: Allergies
      allergies: [], // Array of allergy strings
      
      // Step 9: Snacks
      snacks: '', // 'noTreats', 'fewTreats', 'lotOfTreats'
      
      // Step 10: Email
      ownerName: '',
      ownerEmail: '',
      emailConsent: true,
      profilePhotoUrl: '',
    };
    
    this.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };
    
    this.selectedFlavors = [];
    this.selectedPlan = null; // '1x', '2x', '3x'
    this.selectedPricePeriod = 'day'; // 'day', 'week', 'shipment' - synced between flavors and bulk steps
    this.petMetaobjectId = null; // Shopify Pet Metaobject ID
    this.customerId = null; // Shopify Customer ID
    this.sessionCustomerId = null; // Customer ID preserved across resets for multi-pet flow
    this.sessionOwnerName = null; // Owner name preserved across resets for multi-pet flow
    this.sessionOwnerEmail = null; // Owner email preserved across resets for multi-pet flow
    this.sessionEmailConsent = true; // Email consent preserved across resets for multi-pet flow
    this.variantPricing = {}; // Store variant prices by flavorId: { [flavorId]: { price: amountInCents, compareAtPrice: amountInCents, currency: 'EUR', title: 'Rund' } }
    this.sellingPlanNames = {}; // Cache selling plan names by selling plan ID: { [sellingPlanId]: 'Elke X dagen' }
  }

  /**
   * Update pet data field
   * @param {string} field - Field name
   * @param {*} value - Field value
   */
  updatePetData(field, value) {
    if (field in this.petData) {
      if (field === 'name' && typeof value === 'string') {
        value = normalizePetNameEdges(value);
      }
      this.petData[field] = value;
      this.validateStep(this.currentStep);
    } else {
      console.warn(`Unknown pet data field: ${field}`);
    }
  }

  /**
   * Update multiple pet data fields at once
   * @param {Object} updates - Object with field: value pairs
   */
  updatePetDataBatch(updates) {
    Object.keys(updates).forEach(field => {
      this.updatePetData(field, updates[field]);
    });
  }

  /**
   * Update calculations
   * @param {Object} calculations - Calculation results
   */
  updateCalculations(calculations) {
    this.calculations = { ...this.calculations, ...calculations };
  }

  /**
   * Set current step
   * @param {string} step - Step identifier
   */
  setCurrentStep(step) {
    this.currentStep = step;
  }

  /**
   * Get current step
   * @returns {string} Current step identifier
   */
  getCurrentStep() {
    return this.currentStep;
  }

  /**
   * Validate if current step has required data
   * @param {string} step - Step to validate (defaults to current step)
   * @returns {boolean} True if step is valid
   */
  validateStep(step = this.currentStep) {
    switch (step) {
      case 'name':
        return this.petData.name.trim().length > 0 && this.petData.gender !== '';
      
      case 'spayed':
        return this.petData.spayed !== null;
      
      case 'age':
        return this.petData.ageGroup !== '';
      
      case 'breed':
        return this.petData.breed.trim().length > 0 || this.petData.breedUnknown || this.petData.breedMixed;
      
      case 'weight':
        return this.petData.weight !== null && this.petData.weight > 0;
      
      case 'composition':
        return this.petData.composition !== '';
      
      case 'activity':
        return this.petData.activity !== '';
      
      case 'allergies':
        // Allergies are optional, but if all are selected, it's invalid
        return true; // Validation handled in step component
      
      case 'snacks':
        return this.petData.snacks !== '';
      
      case 'email':
        return this.isValidEmail(this.petData.ownerEmail);
      
      default:
        return true;
    }
  }

  /**
   * Validate email format
   * @param {string} email - Email address
   * @returns {boolean} True if valid email
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get all pet data
   * @returns {Object} Complete pet data object
   */
  getPetData() {
    return { ...this.petData };
  }

  /**
   * Get all calculations
   * @returns {Object} Complete calculations object
   */
  getCalculations() {
    return { ...this.calculations };
  }

  /**
   * Get complete state
   * @returns {Object} Complete state object
   */
  getState() {
    return {
      currentStep: this.currentStep,
      petData: this.getPetData(),
      calculations: this.getCalculations(),
      selectedFlavors: [...this.selectedFlavors],
      selectedPlan: this.selectedPlan,
      selectedPricePeriod: this.selectedPricePeriod,
      petMetaobjectId: this.petMetaobjectId,
      customerId: this.customerId,
      sessionCustomerId: this.sessionCustomerId,
      sessionOwnerName: this.sessionOwnerName,
      sessionOwnerEmail: this.sessionOwnerEmail,
      sessionEmailConsent: this.sessionEmailConsent,
      variantPricing: { ...this.variantPricing },
      sellingPlanNames: { ...this.sellingPlanNames }
    };
  }

  /**
   * Load state from object
   * @param {Object} state - State object to load
   */
  loadState(state) {
    if (state.currentStep) this.currentStep = state.currentStep;
    if (state.petData) this.petData = { ...this.petData, ...state.petData };
    if (typeof this.petData.name === 'string') {
      this.petData.name = normalizePetNameEdges(this.petData.name);
    }
    if (state.calculations) this.calculations = { ...this.calculations, ...state.calculations };
    if (state.selectedFlavors) this.selectedFlavors = [...state.selectedFlavors];
    if (state.selectedPlan) this.selectedPlan = state.selectedPlan;
    if (state.selectedPricePeriod) this.selectedPricePeriod = state.selectedPricePeriod;
    if (state.petMetaobjectId) this.petMetaobjectId = state.petMetaobjectId;
    if (state.customerId) this.customerId = state.customerId;
    if (state.sessionCustomerId) this.sessionCustomerId = state.sessionCustomerId;
    if (state.sessionOwnerName !== undefined) this.sessionOwnerName = state.sessionOwnerName;
    if (state.sessionOwnerEmail !== undefined) this.sessionOwnerEmail = state.sessionOwnerEmail;
    if (state.sessionEmailConsent !== undefined) this.sessionEmailConsent = state.sessionEmailConsent;
    if (state.variantPricing) this.variantPricing = { ...state.variantPricing };
    if (state.sellingPlanNames) this.sellingPlanNames = { ...state.sellingPlanNames };
  }

  /**
   * Reset state to initial values
   */
  reset() {
    // Preserve session values across resets (for multi-pet flow)
    const preservedSessionCustomerId = this.sessionCustomerId;
    const preservedSessionOwnerName = this.sessionOwnerName;
    const preservedSessionOwnerEmail = this.sessionOwnerEmail;
    const preservedSessionEmailConsent = this.sessionEmailConsent;
    
    this.currentStep = 'name';
    this.petData = {
      name: '',
      gender: '',
      spayed: null,
      ageGroup: '',
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      weight: null,
      composition: '',
      activity: '',
      isWorkingDog: false,
      allergies: [],
      snacks: '',
      ownerName: preservedSessionOwnerName || '',
      ownerEmail: preservedSessionOwnerEmail || '',
      emailConsent: preservedSessionEmailConsent !== undefined ? preservedSessionEmailConsent : true
    };
    this.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };
    this.selectedFlavors = [];
    this.selectedPlan = null;
    this.selectedPricePeriod = 'day';
    this.petMetaobjectId = null;
    this.customerId = null;
    // Restore preserved session values
    this.sessionCustomerId = preservedSessionCustomerId;
    this.sessionOwnerName = preservedSessionOwnerName;
    this.sessionOwnerEmail = preservedSessionOwnerEmail;
    this.sessionEmailConsent = preservedSessionEmailConsent;
    this.sellingPlanNames = {};
  }

  /**
   * Clear session customer ID (when different email is entered, logout, or new session)
   */
  clearSessionCustomerId() {
    this.sessionCustomerId = null;
    this.sessionOwnerName = null;
    this.sessionOwnerEmail = null;
    this.sessionEmailConsent = true;
  }

  /**
   * Possessive pronoun (NL: zijn/haar, EN: his/her) from pet gender + storefront locale.
   */
  getPronoun() {
    const locale = (typeof window !== 'undefined' &&
      (window.__wizard1CurrentLocale || window.__wizard2CurrentLocale))
      ? String(window.__wizard1CurrentLocale || window.__wizard2CurrentLocale).toLowerCase()
      : 'nl';
    const en = locale.startsWith('en');
    if (this.petData.gender === 'female') {
      return en ? 'her' : 'haar';
    }
    return en ? 'his' : 'zijn';
  }

  /**
   * Get possessive pronoun based on gender
   * @returns {string} 'haar' or 'zijn'
   */
  getPossessivePronoun() {
    return this.getPronoun();
  }

  /**
   * Find the furthest valid step based on saved state
   * Checks all steps up to and including the saved currentStep,
   * and returns the furthest valid step found.
   * @returns {string} Furthest valid step identifier
   */
  getFurthestValidStep() {
    // Step order in the flow
    const stepOrder = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'flavors', 'plan', 'bulk'
    ];

    // Start from saved currentStep, or default to 'name'
    const savedStep = this.currentStep || 'name';
    const savedStepIndex = stepOrder.indexOf(savedStep);
    
    // If saved step is not in the order, default to 'name'
    if (savedStepIndex === -1) {
      return 'name';
    }

    // Check steps in order up to and including the saved step
    // Return the furthest valid step found
    let furthestValidStep = 'name'; // Default fallback
    
    for (let i = 0; i <= savedStepIndex; i++) {
      const step = stepOrder[i];
      const isValid = this.validateStep(step);
      
      // For calculation step, check if email step is complete
      if (step === 'calculation') {
        if (this.validateStep('email')) {
          furthestValidStep = step;
        } else {
          // Can't proceed to calculation without email
          break;
        }
      }
      // For flavors step, check if calculations exist
      else if (step === 'flavors') {
        const hasCalculations = this.calculations && 
                               this.calculations.dailyFoodG !== null && 
                               this.calculations.dailyFoodG !== undefined;
        if (hasCalculations) {
          furthestValidStep = step;
        } else {
          // Can't proceed to flavors without calculations
          break;
        }
      }
      // For plan and bulk steps, check if calculations and flavors exist
      else if (step === 'plan' || step === 'bulk') {
        const hasCalculations = this.calculations && 
                               this.calculations.dailyFoodG !== null && 
                               this.calculations.dailyFoodG !== undefined;
        const hasFlavors = this.selectedFlavors && this.selectedFlavors.length > 0;
        if (hasCalculations && hasFlavors) {
          furthestValidStep = step;
        } else {
          // Can't proceed to plan/bulk without calculations and flavors
          break;
        }
      }
      // For quiz steps, validate normally
      else {
        if (isValid) {
          furthestValidStep = step;
        } else {
          // If a step is invalid, stop here (can't proceed further)
          break;
        }
      }
    }

    return furthestValidStep;
  }
}

// Export for global access
window.PetFlowState = PetFlowState;

